import React, { useRef, useEffect } from 'react'
import styled from 'styled-components'
import { useConfig } from '@/contexts/ConfigContext'

const Canvas = styled.canvas`
    background-color: rgba(5, 5, 10, 0.6);
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.05);
    image-rendering: pixelated;
    width: 100%;
    max-width: 250px;
    aspect-ratio: 1;
`

const PreviewContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    margin: 20px 0;
    box-shadow: inset 0px 0px 100px #05020879;
`

const Label = styled.div`
    color: var(--font-chakra);
    font-size: 0.9rem;
    color: #6366f1;
    margin-bottom: 8px;
    text-transform: uppercase;
    width: 100%;
    text-align: left;
`

const UndefinedContainer = styled.div`
    width: 100%;
    max-width: 250px;
    aspect-ratio: 1;
    background-color: #000;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 5px;
    border: 1px solid #333;
    animation: fadeIn 0.5s ease;

    @keyframes fadeIn {
        from {
            opacity: 0;
        }
        to {
            opacity: 1;
        }
    }
`

const UndefinedText = styled.span`
    color: #444;
    font-family: monospace;
    font-size: 1.2rem;
    font-weight: bold;
    letter-spacing: 1px;
`

export default function CrosshairPreview({ isActive, minimal = false }) {
    const canvasRef = useRef(null)
    const { configData } = useConfig()
    const [dynamicOffset, setDynamicOffset] = React.useState(0)
    const animationRef = useRef(null)

    const ch = configData.crosshair

    const size = ch?.cl_crosshairsize || 0
    const thickness = ch?.cl_crosshairthickness || 0
    const gap = ch?.cl_crosshairgap || 0
    const colorR = ch?.cl_crosshaircolor_r || 0
    const colorG = ch?.cl_crosshaircolor_g || 0
    const colorB = ch?.cl_crosshaircolor_b || 0
    const alpha = ch?.cl_crosshairalpha || 0
    const dot = ch?.cl_crosshairdot === 1 || ch?.cl_crosshairdot === true
    const outline =
        ch?.cl_crosshair_drawoutline === 1 ||
        ch?.cl_crosshair_drawoutline === true
    const outlineThickness = ch?.cl_crosshair_outlinethickness || 0
    const isTStyle = ch?.cl_crosshair_t === 1 || ch?.cl_crosshair_t === true
    const style = ch?.cl_crosshairstyle

    useEffect(() => {
        if (!isActive || style !== 5) {
            setDynamicOffset(0)
            if (animationRef.current) cancelAnimationFrame(animationRef.current)
            return
        }

        let startTime = Date.now()

        const animate = () => {
            const now = Date.now()
            const time = now - startTime
            const cycleDuration = 2200
            const t = time % cycleDuration

            let currentRecoil = 0
            const baseDynamic = 5

            const addPulse = (start, attack, decay, magnitude) => {
                if (t < start) return 0
                const localT = t - start
                if (localT < attack) return (localT / attack) * magnitude
                if (localT < attack + decay) {
                    const progress = (localT - attack) / decay
                    return magnitude * Math.cos((progress * Math.PI) / 2)
                }
                return 0
            }

            currentRecoil += addPulse(100, 30, 150, 8)
            currentRecoil += addPulse(250, 30, 200, 14)
            currentRecoil += addPulse(380, 20, 1200, 25)

            setDynamicOffset(baseDynamic + currentRecoil)
            animationRef.current = requestAnimationFrame(animate)
        }

        animationRef.current = requestAnimationFrame(animate)

        return () => {
            if (animationRef.current) cancelAnimationFrame(animationRef.current)
        }
    }, [isActive, style])

    useEffect(() => {
        if (!isActive) return

        const canvas = canvasRef.current
        if (!canvas) return
        const ctx = canvas.getContext('2d')
        const dpr = window.devicePixelRatio || 1
        const rect = canvas.getBoundingClientRect()

        canvas.width = rect.width * dpr
        canvas.height = rect.height * dpr
        ctx.scale(dpr, dpr)
        ctx.imageSmoothingEnabled = false

        drawCrosshair(ctx, rect.width, rect.height)
    }, [
        isActive,
        size,
        thickness,
        gap,
        colorR,
        colorG,
        colorB,
        alpha,
        dot,
        outline,
        outlineThickness,
        minimal,
        isTStyle,
        dynamicOffset
    ])

    const calculatePixelSize = (s) => {
        let value = s
        let extraSize = 0
        if (value > 2 && value < 6) extraSize += 1
        else if (value >= 6 && value <= 12) extraSize += 2
        else if (value > 12 && value < 14) extraSize += 3
        else if (value >= 14 && value <= 18) extraSize += 4
        else if (value > 18 && value < 22) extraSize += 5
        else if (value >= 22) extraSize += 6
        return { value: value * 2 + extraSize, extra: extraSize }
    }

    const calculateTickness = (t) => {
        let value = t
        let extraTickness = 0
        if (value > 2 && value < 6) extraTickness += 1
        else if (value >= 6 && value <= 12) extraTickness += 2
        else if (value > 12 && value < 14) extraTickness += 3
        else if (value >= 14 && value <= 18) extraTickness += 4
        else if (value > 18 && value < 22) extraTickness += 5
        else if (value >= 22) extraTickness += 6
        return { value: value * 2 + extraTickness, extra: extraTickness }
    }

    const calculateGap = () => gap + calculateTickness(thickness).value / 2 + 4 + dynamicOffset

    const calculateOutlineThickness = (active, t) => {
        if (!active) return 0
        if (t == 0.5) return 0.5
        return Math.ceil(t)
    }

    const drawCrosshair = (ctx, width, height) => {
        const centerX = Math.floor(width / 2)
        const centerY = Math.floor(height / 2)
        ctx.clearRect(0, 0, width, height)

        const pixelSize = calculatePixelSize(size).value
        const pixelThickness = calculateTickness(thickness).value
        const pixelOutlineThickness = calculateOutlineThickness(
            outline,
            outlineThickness
        )
        const pixelGap = calculateGap()
        const color = `rgba(${colorR}, ${colorG}, ${colorB}, ${alpha / 255})`

        // Shared render logic
        const renderLine = (pos, c, s, g, t, ot) => {
            const totalThickness = t + 2 * ot
            const totalSize = s + 2 * ot
            const halfThickness = t / 2

            ctx.fillStyle = c
            let x, y, w, h

            if (pos === 'left') {
                x = centerX - g - s - ot
                y = centerY - halfThickness - ot
                w = totalSize
                h = totalThickness
            } else if (pos === 'right') {
                x = centerX + g - ot
                y = centerY - halfThickness - ot
                w = totalSize
                h = totalThickness
            } else if (pos === 'top') {
                x = centerX - halfThickness - ot
                y = centerY - g - s - ot
                w = totalThickness
                h = totalSize
            } else if (pos === 'bottom') {
                x = centerX - halfThickness - ot
                y = centerY + g - ot
                w = totalThickness
                h = totalSize
            }
            ctx.fillRect(
                Math.round(x),
                Math.round(y),
                Math.round(w),
                Math.round(h)
            )
        }

        const activeDirs = isTStyle ? ['left', 'right', 'bottom'] : ['left', 'right', 'top', 'bottom']

        activeDirs.forEach((pos) => {
            if (outline && pixelOutlineThickness > 0) {
                renderLine(
                    pos,
                    'rgba(0,0,0,1)',
                    pixelSize,
                    pixelGap,
                    pixelThickness,
                    pixelOutlineThickness
                )
            }
            renderLine(pos, color, pixelSize, pixelGap, pixelThickness, 0)
        })

        if (dot) {
            const totalDotSize = pixelThickness + pixelOutlineThickness * 2
            const halfTotalDotSize = Math.floor(totalDotSize / 2)
            if (pixelOutlineThickness > 0) {
                ctx.fillStyle = 'rgba(0,0,0,1)'
                ctx.fillRect(
                    centerX - halfTotalDotSize,
                    centerY - halfTotalDotSize,
                    totalDotSize,
                    totalDotSize
                )
            }
            const halfDotSize = Math.floor(pixelThickness / 2)
            ctx.fillStyle = color
            ctx.fillRect(
                centerX - halfDotSize,
                centerY - halfDotSize,
                pixelThickness,
                pixelThickness
            )
        }
    }

    if (minimal) {
        return (
            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {isActive ? (
                    <Canvas
                        ref={canvasRef}
                        style={{
                            maxWidth: 'none',
                            width: '100%',
                            height: '100%',
                            border: 'none',
                        }}
                    />
                ) : (
                    <div
                        style={{
                            width: '100%',
                            height: '100%',
                            background: 'rgba(0,0,0,0.2)',
                            borderRadius: '8px',
                            border: '1px solid rgba(255,255,255,0.05)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <span style={{ fontSize: '10px', color: '#444' }}>
                            N/A
                        </span>
                    </div>
                )}
            </div>
        )
    }

    return (
        <PreviewContainer>
            <Label>Crosshair Preview</Label>
            {isActive ? (
                <Canvas ref={canvasRef} />
            ) : (
                <UndefinedContainer>
                    <UndefinedText>undefined</UndefinedText>
                </UndefinedContainer>
            )}
        </PreviewContainer>
    )
}
